/**
 * Subscription tier config + thin wrappers over `./orgSubscription`.
 *
 * All reads of an org's tier/status go through `getOrgSubscription`; the
 * legacy per-membership subscription shape is never referenced here.
 */

import {
  SUBSCRIPTION_TIERS,
  SUBSCRIPTION_STATUS,
  getOrgSubscription,
  getOrgTier,
  isOrgSubscriptionActive,
} from './orgSubscription';

export { SUBSCRIPTION_TIERS, SUBSCRIPTION_STATUS };

// ================ TIER CONFIGURATIONS ================ //

export const TIER_CONFIG = {
  [SUBSCRIPTION_TIERS.FREEMIUM]: {
    id: 'freemium',
    name: 'Freemium',
    description: 'Solo artists, trying out',
    priceUSD: 0,
    price: 0,
    stripePriceId: null,
    priceId: null,
    limits: {
      teamSize: 1,
      maxTeamSize: 1,
      emailCampaigns: 2,
      liveRoomFeePercent: 15,
      storageGB: 1,
      emailSequences: 0,
      automationRules: 0,
      analyticsHistory: 30,
      supportLevel: 'community',
    },
    features: [
      'Live Room creation',
      'Limited newsletter sends',
      'Basic analytics',
      'Community support',
    ],
    restrictions: [
      'No team members',
      'Limited email campaigns',
      'High transaction fees',
      'Basic features only',
    ],
  },

  [SUBSCRIPTION_TIERS.STARTER]: {
    id: 'starter',
    name: 'Starter',
    description: 'Independent creators',
    priceUSD: 15,
    price: 1500,
    stripePriceId: 'price_starter_monthly',
    priceId: 'price_starter_monthly',
    annualPrice: 15000,
    annualPriceId: 'price_starter_annual',
    limits: {
      teamSize: 2,
      maxTeamSize: 2,
      emailCampaigns: 10,
      liveRoomFeePercent: 10,
      storageGB: 5,
      emailSequences: 3,
      automationRules: 5,
      analyticsHistory: 90,
      supportLevel: 'email',
    },
    features: [
      'Up to 2 team members',
      'More email campaigns',
      'Email sequences',
      'Basic automation',
      'Extended analytics',
      'Email support',
    ],
    restrictions: [
      'Limited team size',
      'Basic automation only',
      'Standard transaction fees',
    ],
  },

  [SUBSCRIPTION_TIERS.PRO]: {
    id: 'pro',
    name: 'Pro',
    description: 'Active solo or small team',
    priceUSD: 35,
    price: 3500,
    stripePriceId: 'price_pro_monthly',
    priceId: 'price_pro_monthly',
    annualPrice: 35000,
    annualPriceId: 'price_pro_annual',
    limits: {
      teamSize: 5,
      maxTeamSize: 5,
      emailCampaigns: 50,
      liveRoomFeePercent: 5,
      storageGB: 20,
      emailSequences: 10,
      automationRules: 15,
      analyticsHistory: 180,
      supportLevel: 'priority_email',
    },
    features: [
      'Up to 5 team members',
      'Full email tool access',
      'Assign collaborators',
      'Advanced automation',
      'Extended analytics history',
      'Priority email support',
      'Custom branding',
    ],
    restrictions: [
      'Limited to 5 team members',
      'No phone support',
    ],
  },

  [SUBSCRIPTION_TIERS.ENTERPRISE]: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Large organizations, presenters',
    priceUSD: null,
    price: null,
    stripePriceId: 'price_enterprise_custom',
    priceId: 'price_enterprise_custom',
    limits: {
      teamSize: -1,
      maxTeamSize: -1,
      emailCampaigns: -1,
      liveRoomFeePercent: 0,
      storageGB: -1,
      emailSequences: -1,
      automationRules: -1,
      analyticsHistory: -1,
      supportLevel: 'dedicated',
    },
    features: [
      'Unlimited team members',
      'Custom support',
      'Dedicated account manager',
      'Custom features',
      'API access',
      'White-label solutions',
    ],
    restrictions: [],
  },
};

// ================ CREATOR TIERS ================ //
// Separate from org TIER_CONFIG above. UPDATE.md §"Creator Tiers".
// Keys are prefixed `creator_` so tier lookups never cross-resolve
// with org tiers. Server mirror lives in subscriptionHelpers.js
// (CREATOR_TIER_CONFIG). Keep the two in sync.

export const CREATOR_SUBSCRIPTION_TIERS = Object.freeze({
  FREE: 'creator_free',
  PRO: 'creator_pro',
});

export const CREATOR_TIER_CONFIG = {
  creator_free: {
    id: 'creator_free',
    name: 'Free',
    description: 'Solo creators getting started',
    priceUSD: 0,
    price: 0,
    annualPrice: 0,
    stripePriceId: null,
    annualPriceId: null,
    limits: {
      liveRoomFeePercent: 15,
      newsletterSendsPerMonth: 10,
      liveRoomMaxMinutes: 30,
      replayEnabled: false,
      customUrlStyle: 'path', // attractr.com/username
      prioritySupport: false,
    },
    features: [
      '15% live-room platform fee (10% with org assignment)',
      '10 newsletter sends per month',
      '30-minute live-room sessions',
      'attractr.com/username profile URL',
    ],
  },
  creator_pro: {
    id: 'creator_pro',
    name: 'Pro',
    description: 'Established creators ready to scale',
    priceUSD: 19,
    price: 1900,
    annualPrice: 18900,
    // These IDs are server-side env vars; on the client we identify
    // the active price by the live `stripePriceId` on the user's
    // creatorSubscription record.
    stripePriceId: 'price_creator_pro_monthly',
    annualPriceId: 'price_creator_pro_annual',
    limits: {
      liveRoomFeePercent: 10,
      newsletterSendsPerMonth: -1, // unlimited
      liveRoomMaxMinutes: 240,
      replayEnabled: true,
      customUrlStyle: 'subdomain', // username.onattractr.com
      prioritySupport: true,
    },
    features: [
      '10% live-room platform fee (5% with org assignment)',
      'Unlimited newsletter sends',
      '4-hour live-room sessions + replays',
      'username.onattractr.com subdomain',
      'Priority email support',
    ],
  },
};

export const getCreatorTierConfig = tier =>
  CREATOR_TIER_CONFIG[tier] || CREATOR_TIER_CONFIG.creator_free;

export const getCreatorSubscription = user =>
  user?.attributes?.profile?.privateData?.creatorSubscription || null;

export const getCreatorTier = user =>
  getCreatorSubscription(user)?.tier || CREATOR_SUBSCRIPTION_TIERS.FREE;

// ================ PUBLIC API ================ //

export const getOrganizationSubscription = (user, orgId) =>
  getOrgSubscription(user, orgId);

export const getTierConfig = tier =>
  TIER_CONFIG[tier] || TIER_CONFIG[SUBSCRIPTION_TIERS.FREEMIUM];

export const getSubscriptionLimits = (user, orgId) =>
  getTierConfig(getOrgTier(user, orgId))?.limits || {};

export const isSubscriptionActive = (user, orgId) =>
  isOrgSubscriptionActive(user, orgId);

export const canInviteTeamMember = (user, orgId, currentTeamCount) => {
  if (!isSubscriptionActive(user, orgId)) return false;
  const limits = getSubscriptionLimits(user, orgId);
  const max = limits.teamSize ?? limits.maxTeamSize ?? 1;
  if (max === -1) return true;
  return (currentTeamCount + 1) <= max;
};

export const canCreateEmailCampaign = (user, orgId, currentMonthCount) => {
  if (!isSubscriptionActive(user, orgId)) return false;
  const limits = getSubscriptionLimits(user, orgId);
  const max = limits.emailCampaigns ?? 0;
  if (max === -1) return true;
  return currentMonthCount < max;
};

export const hasFeature = (user, orgId, featureKey) => {
  const tier = getOrgTier(user, orgId);
  const config = getTierConfig(tier);
  const features = config?.features;
  if (!features) return false;
  if (Array.isArray(features)) return features.includes(featureKey);
  return !!features[featureKey];
};

export const getUpgradeOptions = currentTier => {
  const tiers = Object.keys(TIER_CONFIG);
  const idx = tiers.indexOf(currentTier);
  if (idx === -1) return [];
  return tiers.slice(idx + 1).map(tier => ({
    tier,
    config: TIER_CONFIG[tier],
  }));
};

export const getStripePriceId = tier =>
  TIER_CONFIG[tier]?.stripePriceId || null;

export const getTierPrice = tier =>
  TIER_CONFIG[tier]?.priceUSD ?? 0;

/**
 * Map a Stripe subscription payload to our internal subscription shape.
 * The returned object is suitable to pass to `buildSetOrgSubscriptionPatch`.
 */
export const mapStripeSubscription = stripeData => {
  if (!stripeData) {
    return {
      tier: SUBSCRIPTION_TIERS.FREEMIUM,
      status: SUBSCRIPTION_STATUS.ACTIVE,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      currentPeriodStart: null,
      currentPeriodEnd: null,
    };
  }

  const priceId = stripeData?.items?.data?.[0]?.price?.id || null;

  let tier = SUBSCRIPTION_TIERS.FREEMIUM;
  for (const [tierKey, config] of Object.entries(TIER_CONFIG)) {
    if (config.stripePriceId && priceId && priceId.includes(config.stripePriceId)) {
      tier = tierKey;
      break;
    }
  }

  const toIso = sec =>
    typeof sec === 'number' ? new Date(sec * 1000).toISOString() : null;

  return {
    tier,
    status: stripeData.status || SUBSCRIPTION_STATUS.ACTIVE,
    stripeCustomerId: stripeData.customer || null,
    stripeSubscriptionId: stripeData.id || null,
    currentPeriodStart: toIso(stripeData.current_period_start),
    currentPeriodEnd: toIso(stripeData.current_period_end),
  };
};
